import * as i0 from '@angular/core';
import { EventEmitter, inject, Output, Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { animationFadeInOut, animationScaleUp } from '@engineering11/ui-lib/e11-animations';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import * as i1 from '@angular/platform-browser';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@engineering11/ui-lib/e11-image-viewer';
import { E11ImageViewerModule } from '@engineering11/ui-lib/e11-image-viewer';
import * as i4 from '@engineering11/ui-lib/e11-directives';
import { E11DirectivesModule } from '@engineering11/ui-lib/e11-directives';
import * as i5 from '@engineering11/ui-lib/e11-closer';
import { E11CloserModule } from '@engineering11/ui-lib/e11-closer';

var FileTypeEnum;
(function (FileTypeEnum) {
    FileTypeEnum["NotSupported"] = "NotSupported";
    FileTypeEnum["Image"] = "Image";
    FileTypeEnum["Audio"] = "Audio";
    FileTypeEnum["Video"] = "Video";
    FileTypeEnum["IFrame"] = "IFrame";
})(FileTypeEnum || (FileTypeEnum = {}));
function getFormattedUrl(url, filename) {
    const fileExtension = getFileExtensionByTitle(filename);
    switch (fileExtension) {
        case 'png':
        case 'jpg':
        case 'jpeg':
        case 'gif':
            return {
                url: url,
                type: FileTypeEnum.Image,
            };
        case 'mp3':
            return {
                url: url,
                type: FileTypeEnum.Audio,
            };
        case 'pdf':
            return {
                url: url,
                type: FileTypeEnum.IFrame,
            };
        case 'mp4':
            return {
                url: url,
                type: FileTypeEnum.Video,
            };
        case 'ppt':
        case 'pptx':
        case 'doc':
        case 'docx':
        case 'xls':
        case 'xlsx':
            return {
                url: `https://view.officeapps.live.com/op/embed.aspx?src=${decodeURIComponent(url)}`,
                type: FileTypeEnum.IFrame,
            };
        default:
            return {
                url: '',
                type: FileTypeEnum.NotSupported,
            };
    }
}
// TODO: Move to utility library
const getFileExtensionByTitle = (filename) => {
    return filename && typeof filename === 'string' ? filename?.split('.')?.pop()?.toLowerCase() : null;
};

/**
 * TODO: Figure out how to translate the audio error message
 * TODO: Figure out the best UX for not supported files - is it opening the URL?? Or something else
 * TODO: Animate & manage open/close
 * TODO: Close on click outside
 */
class E11FileViewerComponent {
    constructor(sanitizer, elementRef) {
        this.sanitizer = sanitizer;
        this.elementRef = elementRef;
        this.close = new EventEmitter();
        this.isOpened = false;
        this.fileTypeSupported = true;
        this.FileTypeEnum = FileTypeEnum;
        this.isBrowser = inject(IS_BROWSER);
    }
    ngOnInit() {
        if (!this.isBrowser)
            return;
        if (this.url && this.name) {
            // we should do the file checking right here so that we can view different type of file
            this.formattedUrl = getFormattedUrl(this.url, this.name);
            this.fileTypeSupported = this.formattedUrl?.type !== FileTypeEnum.NotSupported;
            if (!this.fileTypeSupported) {
                window.open(this.url, '_blank');
                this.closeAction();
            }
        }
    }
    closeAction() {
        setTimeout(() => {
            this.close.emit(true);
        }, 100);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11FileViewerComponent, deps: [{ token: i1.DomSanitizer }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11FileViewerComponent, selector: "e11-file-viewer", inputs: { url: "url", name: "name" }, outputs: { close: "close" }, ngImport: i0, template: `
    <e11-image-viewer
      *ngIf="formattedUrl?.type === FileTypeEnum.Image"
      [openOnInit]="true"
      [imageUrl]="formattedUrl ? formattedUrl.url : ''"
      (close)="closeAction()"
    >
    </e11-image-viewer>

    <div
      *ngIf="formattedUrl?.type !== FileTypeEnum.Image"
      class="viewer-transition viewer-backdrop e11-fixed e11-animated e11-fadeIn e11-overscroll-none e11-flex e11-top-0 e11-left-0 e11-h-screen e11-w-full "
      (closeOnEscape)="closeAction()"
    >
      <div
        class="image-container e11-h-screen e11-flex e11-justify-center e11-items-center e11-relative e11-m-auto e11-w-full md:e11-w-auto e11-p-0 md:e11-p-5 e11-max-w-1/2"
      >
        <ng-container [ngSwitch]="formattedUrl?.type">
          <ng-container *ngSwitchCase="FileTypeEnum.Audio">
            <div class="e11-relative">
              <audio controls>
                <source [src]="formattedUrl && sanitizer.bypassSecurityTrustResourceUrl(formattedUrl.url)" type="audio/mpeg" />
                <!-- fallback for non supporting browsers goes here -->
                <p>
                  {{ 'Your browser does not support HTML audio, but you can still ' }}
                  <a [href]="formattedUrl && formattedUrl.url" target="_blank">{{ 'download the music' }}</a
                  >.
                </p>
              </audio>
              <e11-closer
                (close)="closeAction()"
                [size]="'md'"
                [classOverrides]="
                  'e11-absolute e11-cursor-pointer e11-top-0 e11-right-0 md:e11--top-2 md:e11--right-2 e11-h-5 e11-w-5 e11-rounded-full'
                "
              ></e11-closer>
            </div>
          </ng-container>
          <ng-container *ngSwitchDefault>
            <div class="iframe-box e11-relative">
              <iframe
                [title]="name"
                [src]="formattedUrl && sanitizer.bypassSecurityTrustResourceUrl(formattedUrl.url)"
                frameborder="0"
                class="e11-w-full e11-h-full"
              ></iframe>
              <e11-closer
                [size]="'md'"
                (close)="closeAction()"
                [classOverrides]="
                  'e11-absolute e11-cursor-pointer e11-top-0 e11-right-0 md:e11--top-2 md:e11--right-2 e11-h-5 e11-w-5 e11-rounded-full'
                "
              ></e11-closer>
            </div>
          </ng-container>
        </ng-container>
      </div>
    </div>
  `, isInline: true, styles: [".iframe-box{height:82vh;width:90vh}.image-container{height:auto}.viewer-backdrop{background-color:#00000080;z-index:9999}.viewer-transition{transition:all .5s}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i2.NgSwitchDefault, selector: "[ngSwitchDefault]" }, { kind: "component", type: i3.E11ImageViewerComponent, selector: "e11-image-viewer", inputs: ["imageUrl", "openOnInit", "initialViewIndex"], outputs: ["close"] }, { kind: "directive", type: i4.EscapeKeyDirective, selector: "[closeOnEscape]", outputs: ["closeOnEscape"] }, { kind: "component", type: i5.E11CloserComponent, selector: "e11-closer", inputs: ["classOverrides", "icon", "size", "darkMode"], outputs: ["close"] }], animations: [animationFadeInOut(), animationScaleUp()], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11FileViewerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-file-viewer', template: `
    <e11-image-viewer
      *ngIf="formattedUrl?.type === FileTypeEnum.Image"
      [openOnInit]="true"
      [imageUrl]="formattedUrl ? formattedUrl.url : ''"
      (close)="closeAction()"
    >
    </e11-image-viewer>

    <div
      *ngIf="formattedUrl?.type !== FileTypeEnum.Image"
      class="viewer-transition viewer-backdrop e11-fixed e11-animated e11-fadeIn e11-overscroll-none e11-flex e11-top-0 e11-left-0 e11-h-screen e11-w-full "
      (closeOnEscape)="closeAction()"
    >
      <div
        class="image-container e11-h-screen e11-flex e11-justify-center e11-items-center e11-relative e11-m-auto e11-w-full md:e11-w-auto e11-p-0 md:e11-p-5 e11-max-w-1/2"
      >
        <ng-container [ngSwitch]="formattedUrl?.type">
          <ng-container *ngSwitchCase="FileTypeEnum.Audio">
            <div class="e11-relative">
              <audio controls>
                <source [src]="formattedUrl && sanitizer.bypassSecurityTrustResourceUrl(formattedUrl.url)" type="audio/mpeg" />
                <!-- fallback for non supporting browsers goes here -->
                <p>
                  {{ 'Your browser does not support HTML audio, but you can still ' }}
                  <a [href]="formattedUrl && formattedUrl.url" target="_blank">{{ 'download the music' }}</a
                  >.
                </p>
              </audio>
              <e11-closer
                (close)="closeAction()"
                [size]="'md'"
                [classOverrides]="
                  'e11-absolute e11-cursor-pointer e11-top-0 e11-right-0 md:e11--top-2 md:e11--right-2 e11-h-5 e11-w-5 e11-rounded-full'
                "
              ></e11-closer>
            </div>
          </ng-container>
          <ng-container *ngSwitchDefault>
            <div class="iframe-box e11-relative">
              <iframe
                [title]="name"
                [src]="formattedUrl && sanitizer.bypassSecurityTrustResourceUrl(formattedUrl.url)"
                frameborder="0"
                class="e11-w-full e11-h-full"
              ></iframe>
              <e11-closer
                [size]="'md'"
                (close)="closeAction()"
                [classOverrides]="
                  'e11-absolute e11-cursor-pointer e11-top-0 e11-right-0 md:e11--top-2 md:e11--right-2 e11-h-5 e11-w-5 e11-rounded-full'
                "
              ></e11-closer>
            </div>
          </ng-container>
        </ng-container>
      </div>
    </div>
  `, animations: [animationFadeInOut(), animationScaleUp()], encapsulation: ViewEncapsulation.None, styles: [".iframe-box{height:82vh;width:90vh}.image-container{height:auto}.viewer-backdrop{background-color:#00000080;z-index:9999}.viewer-transition{transition:all .5s}\n"] }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }, { type: i0.ElementRef }], propDecorators: { url: [{
                type: Input
            }], name: [{
                type: Input
            }], close: [{
                type: Output
            }] } });

class E11FileViewerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11FileViewerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11FileViewerModule, declarations: [E11FileViewerComponent], imports: [CommonModule, E11ImageViewerModule, E11DirectivesModule, E11CloserModule, E11UtilModule], exports: [E11FileViewerComponent, E11DirectivesModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11FileViewerModule, imports: [CommonModule, E11ImageViewerModule, E11DirectivesModule, E11CloserModule, E11UtilModule, E11DirectivesModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11FileViewerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11FileViewerComponent],
                    imports: [CommonModule, E11ImageViewerModule, E11DirectivesModule, E11CloserModule, E11UtilModule],
                    exports: [E11FileViewerComponent, E11DirectivesModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11FileViewerComponent, E11FileViewerModule };
